/**
 * Resolves colour slots against the jobshop palette (JET-2593).
 *
 * Every colour in a content-service payload is `{ key, label, value }`, where `key` names
 * a palette slot (`custom-color.3`, `default-black`) and `value` is the hex content-service
 * resolved for it. Resolving on this side means the hex comes from the `fonts-and-colors`
 * palette instead, so a cached payload holds a slot reference rather than a frozen colour.
 *
 * Only `value` is rewritten; `key` and `label` are left alone. A `key` that is not in the
 * palette keeps the value the backend sent, so the transform can only correct a colour,
 * never blank one.
 *
 * Unconditional on purpose: content-service resolves `value` from this same palette today,
 * so resolving here recomputes the identical hex. That makes it safe to ship ahead of the
 * backend dropping its own resolution, with deploy order as the only ordering constraint —
 * no flag, so rendering never depends on a flag service being reachable.
 */

/** A palette entry, narrowed to the slots that actually carry a hex. */
export type ColorSlot = ColorValue & { value: string }

/**
 * Indexes a jobshop's `fonts-and-colors` palette by slot key. Empty when absent.
 *
 * A slot only enters the palette if it can actually supply a colour. The palette holds the
 * stored hex rather than a resolved output — content-service substitutes the slot's factory
 * default before serialising, so a live jobshop always carries one — but a slot whose value
 * arrived blank must not be indexed, or every colour referencing it would resolve to an
 * empty string and lose its colour. Leaving it out keeps the value the backend sent.
 */
export function paletteByKey(jobShop: JobShop | null | undefined): Record<string, ColorSlot> {
  const colors = jobShop?.["fonts-and-colors"]?.config?.general?.colors
  if (!colors)
    return {}

  const palette: Record<string, ColorSlot> = {}
  for (const color of Object.values<ColorValue>(colors)) {
    if (typeof color?.key === "string" && typeof color.value === "string" && color.value !== "")
      palette[color.key] = color as ColorSlot
  }
  return palette
}

/**
 * Returns `payload` with every colour slot's `value` taken from `palette`.
 *
 * Never mutates: `useDataCacheWithStale` hands back the shared cross-request cache object
 * on a hit, so writing into it would both leak between requests and bake the hex back into
 * the cache this change exists to keep symbolic. Subtrees containing no changed colour are
 * returned by reference, so an unaffected payload costs a walk and no copies.
 */
export function resolveColorSlots<T>(payload: T, palette: Record<string, ColorSlot>): T {
  if (!payload || Object.keys(palette).length === 0)
    return payload

  return walk(payload, palette) as T
}

function walk(node: any, palette: Record<string, ColorSlot>): any {
  if (Array.isArray(node)) {
    let changed = false
    const next = node.map((item) => {
      const resolved = walk(item, palette)
      if (resolved !== item)
        changed = true
      return resolved
    })
    return changed ? next : node
  }

  if (node === null || typeof node !== "object")
    return node

  const slotValue = slotValueFor(node, palette)
  if (slotValue !== undefined)
    return slotValue === node.value ? node : { ...node, value: slotValue }

  let changed = false
  const next: Record<string, any> = {}
  for (const key of Object.keys(node)) {
    const resolved = walk(node[key], palette)
    if (resolved !== node[key])
      changed = true
    next[key] = resolved
  }
  return changed ? next : node
}

/**
 * The palette hex for `node`, or undefined when `node` is not a colour slot we know.
 *
 * `value` is optional so this keeps working once the backend stops sending a resolved hex —
 * it may then be an empty string or absent, and an absent one is added. What actually keeps
 * other shapes out is palette membership: `key` has to be one of the 25 slot names, which
 * `LabelValueDto` (`{ label, value }`) and the other option shapes never produce.
 */
function slotValueFor(node: any, palette: Record<string, ColorSlot>): string | undefined {
  if (typeof node.key !== "string" || !("label" in node))
    return undefined

  if ("value" in node && typeof node.value !== "string")
    return undefined

  return palette[node.key]?.value
}
